import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { closuresAPI, batchesAPI } from '../utils/api';
import { tField } from '../utils/bilingualHelper';
import BilingualField from '../components/common/BilingualField';

const FMT_DATE = d => {
  if (!d) return '';
  const str = typeof d === 'string' ? d.split('T')[0] : '';
  if (str && str.includes('-')) {
    const [y, m, day] = str.split('-').map(Number);
    return new Date(y, m - 1, day, 12).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getTypeBadgeInfo = (t) => {
  switch ((t || '').toLowerCase()) {
    case 'holiday': return { label: '🚫 Holiday / 假期', bg: '#f6ffed', color: '#389e0d', border: '#b7eb8f' };
    case 'emergency': return { label: '⚠️ Emergency / 緊急停課', bg: '#fff1f0', color: '#cf1322', border: '#ffa39e' };
    case 'rescheduled': return { label: '📅 Rescheduled / 重新安排', bg: '#fff7e6', color: '#d46b08', border: '#ffd591' };
    case 'makeup': return { label: '🔁 Make-up Class / 補課', bg: '#f9f0ff', color: '#722ed1', border: '#d3adf7' };
    case 'announcement': return { label: '📢 Announcement / 特別通知', bg: '#e6f7ff', color: '#096dd9', border: '#91d5ff' };
    default: return { label: (t || '').toUpperCase(), bg: '#f5f5f5', color: '#595959', border: '#d9d9d9' };
  }
};

export default function Closures() {
  const { t } = useTranslation();
  const [closures, setClosures] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form State
  const [date, setDate] = useState('');
  const [type, setType] = useState('holiday');
  const [reason, setReason] = useState({ en: '', zh: '' });
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [scope, setScope] = useState('all');
  const [affectedBatchIds, setAffectedBatchIds] = useState([]);
  const [notifyParents, setNotifyParents] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Preview State
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const fetchIdRef = React.useRef(0);
  const previewIdRef = React.useRef(0);

  useEffect(() => {
    let isMounted = true;
    loadData(isMounted);
    return () => { isMounted = false; };
  }, []);

  const loadData = async (isMounted = true) => {
    const currentId = ++fetchIdRef.current;
    setLoading(true);
    try {
      const [cRes, bRes] = await Promise.all([
        closuresAPI.getAll(),
        batchesAPI.getAll()
      ]);
      if (!isMounted || currentId !== fetchIdRef.current) return;
      setClosures(cRes.data.closures || []);
      setBatches(bRes.data || []);
    } catch (err) {
      if (!isMounted || currentId !== fetchIdRef.current) return;
      console.error(err);
      alert('Failed to load closures');
    } finally {
      if (isMounted && currentId === fetchIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const targetDate = type === 'makeup' ? (rescheduleDate || date) : date;
    if (targetDate) {
      const currentId = ++previewIdRef.current;
      setLoadingPreview(true);
      closuresAPI.getPreview(
        targetDate, 
        scope, 
        affectedBatchIds,
        (type === 'rescheduled' || type === 'makeup') ? rescheduleDate : undefined,
        type
      )
        .then(res => { 
          if (isMounted && currentId === previewIdRef.current) setPreviewData(res.data); 
        })
        .catch(() => { 
          if (isMounted && currentId === previewIdRef.current) setPreviewData(null); 
        })
        .finally(() => {
          if (isMounted && currentId === previewIdRef.current) setLoadingPreview(false);
        });
    } else {
      setPreviewData(null);
    }
    return () => { isMounted = false; };
  }, [date, rescheduleDate, type, scope, affectedBatchIds]);

  const handleBatchToggle = (id) => {
    if (affectedBatchIds.includes(id)) {
      setAffectedBatchIds(affectedBatchIds.filter(b => b !== id));
    } else {
      setAffectedBatchIds([...affectedBatchIds, id]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const effectiveClosureDate = type === 'makeup' ? (date || rescheduleDate) : date;
    if (!effectiveClosureDate || !reason.en || !reason.zh || !type) return alert('Please fill all required fields (English and Chinese reasons)');
    if ((type === 'rescheduled' || type === 'makeup') && !rescheduleDate) return alert('Reschedule / Make-up date is required');
    if (scope === 'specific' && affectedBatchIds.length === 0) return alert('Please select at least one batch');

    let confirmMsg = `Are you sure you want to declare this ${type}?`;
    if (previewData && previewData.totalBatches > 0) {
      if (type === 'makeup') {
        if (previewData.totalStudents > 0) {
          confirmMsg += `\n\nImpact: ${previewData.totalBatches} batch(es) and ${previewData.totalStudents} eligible student(s) who missed class will be notified.`;
        } else {
          confirmMsg += `\n\nNotice: 0 students currently have unfulfilled leaves or missed classes in the selected batch(es). No student make-up notifications will be broadcast.`;
        }
      } else {
        confirmMsg += `\n\nImpact: ${previewData.totalBatches} batch(es) and ${previewData.totalStudents} student(s) will be affected.`;
      }
    }
    if (notifyParents) {
      confirmMsg += `\nPush notifications WILL be sent to parents.`;
    }

    if (!window.confirm(confirmMsg)) return;

    setSubmitting(true);
    try {
      await closuresAPI.create({
        date: effectiveClosureDate,
        type,
        reason,
        rescheduleDate: (type === 'rescheduled' || type === 'makeup') ? rescheduleDate : null,
        scope,
        batchIds: scope === 'specific' ? affectedBatchIds : [],
        notifyParents
      });
      alert('Closure declared successfully!');
      
      // Reset form
      setDate('');
      setType('holiday');
      setReason({ en: '', zh: '' });
      setRescheduleDate('');
      setScope('all');
      setAffectedBatchIds([]);
      setNotifyParents(false);
      setPreviewData(null);
      
      loadData();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to declare closure');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this closure? Attendance records will be un-marked.')) return;
    try {
      await closuresAPI.delete(id);
      loadData();
    } catch (err) {
      alert('Failed to delete closure');
    }
  };

  return (
    <Layout title={t('closures.title')}>
      <div className="grid-2" style={{ alignItems: 'flex-start' }}>
        
        {/* Left Column: Declare Closure Form */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3 className="dash-card-title">{t('closures.declareNew')}</h3>
          </div>
          
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '0 4px' }}>
            <div className="form-group mb-0">
              <label className="form-label-style">{t('closures.closureType', 'Closure / Event Type')}</label>
              <select className="form-input-style" value={type} onChange={e => setType(e.target.value)}>
                <option value="holiday">🚫 {t('closures.typeHoliday', 'Holiday / 假期')}</option>
                <option value="emergency">⚠️ {t('closures.typeEmergency', 'Emergency / Unexpected / 突發停課')}</option>
                <option value="rescheduled">📅 {t('closures.typeRescheduled', 'Rescheduled Class / 更改上課時間')}</option>
                <option value="makeup">🔁 {t('closures.typeMakeup', 'Make-up Class / 補課')}</option>
                <option value="announcement">📢 {t('closures.typeAnnouncement', 'General Announcement / 公告')}</option>
              </select>

              {/* Explanatory notice box matching Flutter app logic */}
              <div style={{ fontSize: '12px', marginTop: '6px', padding: '8px 10px', borderRadius: '6px', background: '#f8f9fa', border: '1px solid #e9ecef', color: '#555' }}>
                {type === 'rescheduled' && (
                  <span>📢 <strong>Rescheduled Class:</strong> Notifies <u>ALL enrolled students</u> in the affected batch about the date change.</span>
                )}
                {type === 'makeup' && (
                  <span>👥 <strong>Make-up Class:</strong> Notifies <u>ONLY specific students</u> who were on Leave and have unfulfilled leave credits.</span>
                )}
                {type === 'holiday' && (
                  <span>🚫 <strong>Holiday:</strong> Marks Holiday status for scheduled classes on this date.</span>
                )}
                {type === 'emergency' && (
                  <span>⚠️ <strong>Emergency Closure:</strong> Sends an urgent cancellation alert to all affected students & parents.</span>
                )}
                {type === 'announcement' && (
                  <span>📢 <strong>Announcement:</strong> Broadcasts a general announcement notice for the target date.</span>
                )}
              </div>
            </div>

            {(type === 'rescheduled' || type === 'makeup') && (
              <div className="form-group mb-0">
                <label className="form-label-style">{type === 'makeup' ? 'Make-up Class Date / 補課日期 *' : `${t('closures.rescheduleDate', 'Rescheduled Class Date')} *`}</label>
                <input 
                  type="date" 
                  className="form-input-style" 
                  value={rescheduleDate} 
                  onChange={e => setRescheduleDate(e.target.value)} 
                  required
                />
              </div>
            )}

            <div className="form-group mb-0">
              <label className="form-label-style">{t('closures.scope')}</label>
              <select className="form-input-style" value={scope} onChange={e => setScope(e.target.value)}>
                <option value="all">{t('closures.allBatches')}</option>
                <option value="specific">{t('closures.specificBatches')}</option>
              </select>
            </div>

            {scope === 'specific' && (
              <div className="form-group mb-0">
                <label className="form-label-style">{t('closures.selectAffected')}</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto', padding: '10px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee' }}>
                  {batches.map(b => (
                    <label key={b._id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={affectedBatchIds.includes(b._id)} 
                        onChange={() => handleBatchToggle(b._id)} 
                      />
                      {tField(b.name)} ({b.level})
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group mb-0">
              <label className="form-label-style">
                {type === 'makeup' ? 'Target Missed Date (Optional)' : (type === 'announcement' ? 'Target Date / 目標日期 *' : `${t('closures.closureDate')} *`)}
              </label>
              <input 
                type="date" 
                className="form-input-style" 
                value={date} 
                onChange={e => setDate(e.target.value)} 
                required={type !== 'makeup'} 
              />
            </div>

            {/* ── Existing Schedule Alert on Reschedule/Make-up Date ── */}
            {previewData && previewData.existingClosuresOnRescheduleDate?.length > 0 && rescheduleDate && (
              <div style={{
                background: '#fffbe6',
                border: '1.5px solid #ffe58f',
                borderRadius: '10px',
                padding: '12px 14px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, color: '#d46b08', fontSize: '13px', marginBottom: '8px' }}>
                  <span>⚠️ Existing Schedule on {FMT_DATE(rescheduleDate)}:</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {previewData.existingClosuresOnRescheduleDate.map(c => {
                    const badge = getTypeBadgeInfo(c.type);
                    const reasonStr = tField(c.reason) || 'Closure';
                    const scopeStr = c.scope === 'all'
                      ? 'All Batches (Whole Academy)'
                      : (c.affectedBatchNames?.length > 0
                          ? `Batches: ${c.affectedBatchNames.map(b => tField(b)).join(', ')}`
                          : 'Specific Batches');
                    return (
                      <div key={c.id || c._id} style={{ background: '#fff', border: '1px solid #ffd591', borderRadius: '8px', padding: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: badge.bg, color: badge.color, border: `1px solid ${badge.border}` }}>
                            {badge.label}
                          </span>
                          <strong style={{ fontSize: '13px', color: '#262626' }}>{reasonStr}</strong>
                        </div>
                        <div style={{ fontSize: '12px', color: '#595959', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                          <span>🏷️ {scopeStr}</span>
                          {c.rescheduleDate && (
                            <span style={{ color: '#389e0d', fontWeight: 600, marginLeft: '8px' }}>
                              ↳ Rescheduled date: {FMT_DATE(c.rescheduleDate)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Existing Closure Alert on Closure Date ── */}
            {previewData && previewData.existingClosuresOnDate?.length > 0 && date && (
              <div style={{
                background: type === 'rescheduled' ? '#e6f7ff' : '#fffbe6',
                border: `1.5px solid ${type === 'rescheduled' ? '#91d5ff' : '#ffe58f'}`,
                borderRadius: '10px',
                padding: '12px 14px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, color: type === 'rescheduled' ? '#096dd9' : '#d46b08', fontSize: '13px', marginBottom: '8px' }}>
                  <span>
                    {type === 'rescheduled' 
                      ? `ℹ️ Existing closure on ${FMT_DATE(date)} will be updated to Rescheduled:`
                      : `ℹ️ Existing Closure on ${FMT_DATE(date)}:`}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {previewData.existingClosuresOnDate.map(c => {
                    const badge = getTypeBadgeInfo(c.type);
                    const reasonStr = tField(c.reason) || 'Closure';
                    const scopeStr = c.scope === 'all'
                      ? 'All Batches (Whole Academy)'
                      : (c.affectedBatchNames?.length > 0
                          ? `Batches: ${c.affectedBatchNames.map(b => tField(b)).join(', ')}`
                          : 'Specific Batches');
                    return (
                      <div key={c.id || c._id} style={{ background: '#fff', border: `1px solid ${type === 'rescheduled' ? '#91d5ff' : '#ffd591'}`, borderRadius: '8px', padding: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: badge.bg, color: badge.color, border: `1px solid ${badge.border}` }}>
                            {badge.label}
                          </span>
                          <strong style={{ fontSize: '13px', color: '#262626' }}>{reasonStr}</strong>
                        </div>
                        <div style={{ fontSize: '12px', color: '#595959', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                          <span>🏷️ {scopeStr}</span>
                          {c.rescheduleDate && (
                            <span style={{ color: '#389e0d', fontWeight: 600, marginLeft: '8px' }}>
                              ↳ Rescheduled date: {FMT_DATE(c.rescheduleDate)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Batch Impact Preview Breakdown ── */}
            {loadingPreview && (
              <div style={{ fontSize: '12px', color: '#888', fontStyle: 'italic' }}>
                ⏳ Checking impact preview...
              </div>
            )}

            {previewData && !loadingPreview && (
              <div style={{
                padding: '12px 14px',
                borderRadius: '10px',
                background: '#fafafa',
                border: '1px solid #e8e8e8'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: previewData.totalBatches > 0 ? '10px' : '0' }}>
                  <strong style={{ fontSize: '13px', color: '#1f1f1f' }}>
                    📊 Impact Preview {previewData.dayOfWeek ? `(${previewData.dayOfWeek})` : ''}
                  </strong>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--wine, #c04060)' }}>
                    {previewData.totalBatches} {previewData.totalBatches === 1 ? 'Batch' : 'Batches'} • {previewData.totalStudents} {type === 'makeup' ? 'Eligible on Leave' : (previewData.totalStudents === 1 ? 'Student' : 'Students')}
                  </span>
                </div>

                {previewData.totalBatches === 0 ? (
                  <div style={{ fontSize: '12px', color: '#389e0d', background: '#f6ffed', border: '1px solid #b7eb8f', padding: '8px 12px', borderRadius: '6px', marginTop: '6px' }}>
                    ✅ No regular classes run on this day.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '160px', overflowY: 'auto' }}>
                    {previewData.affectedBatches?.map(b => (
                      <div key={b.id} style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '6px', padding: '8px 10px', fontSize: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, color: '#262626' }}>
                          <span>{tField(b.rawName || b.name)}</span>
                          <span style={{ color: '#722ed1', fontWeight: 600 }}>👥 {b.studentCount} {type === 'makeup' ? 'eligible on leave' : 'students'}</span>
                        </div>
                        <div style={{ color: '#8c8c8c', marginTop: '3px', display: 'flex', justifyContent: 'space-between', fontSize: '11.5px' }}>
                          <span>👤 {b.tutorName}</span>
                          <span>⏰ {b.startTime || ''} - {b.endTime || ''}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <BilingualField 
              label={`${t('closures.reason', 'Reason / Title')} *`}
              fieldName="reason"
              value={reason}
              onChange={(_, lang, val) => setReason(p => ({ ...p, [lang]: val }))}
              placeholder={t('closures.reasonPh', 'e.g. Heavy Rain, Public Holiday, Typhoon')}
            />

            <div className="form-group mb-0" style={{ background: 'rgba(192, 64, 96, 0.05)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(192, 64, 96, 0.1)' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  style={{ marginTop: '4px', width: '16px', height: '16px' }} 
                  checked={notifyParents} 
                  onChange={e => setNotifyParents(e.target.checked)} 
                />
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--wine)', fontSize: '14px' }}>{t('closures.sendPush')}</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{t('closures.notifySub')}</div>
                </div>
              </label>
            </div>

            <button type="submit" className="btn btn-primary" style={{ marginTop: '10px' }} disabled={submitting}>
              {submitting ? t('closures.declaring') : t('closures.declareBtn')}
            </button>
          </form>
        </div>

        {/* Right Column: History */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3 className="dash-card-title">{t('closures.history')}</h3>
            <span className="badge badge-blue">{closures.length} Total</span>
          </div>

          {loading ? (
            <div className="dash-loading">⏳ {t('closures.loading')}</div>
          ) : closures.length === 0 ? (
            <div className="dash-empty">
              <div className="dash-empty-icon">🏖️</div>
              <div className="dash-empty-title">{t('closures.noClosures')}</div>
              <p className="dash-empty-sub">{t('closures.noClosuresSub')}</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>{t('closures.date')}</th>
                    <th>{t('closures.typeTh')}</th>
                    <th>{t('closures.reasonTh')}</th>
                    <th>{t('closures.scopeTh')}</th>
                    <th>{t('closures.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {closures.map(c => (
                    <tr key={c._id}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 600, color: '#1a1a2e' }}>{FMT_DATE(c.date)}</div>
                        {(c.type === 'rescheduled' || c.type === 'makeup') && c.rescheduleDate && (
                          <div style={{ fontSize: '11px', color: '#c04060', marginTop: '2px', fontWeight: 600 }}>
                            ↳ {t('closures.to', 'To:')} {FMT_DATE(c.rescheduleDate)}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${
                          c.type === 'holiday' ? 'badge-green' : 
                          c.type === 'emergency' ? 'badge-red' : 
                          c.type === 'rescheduled' ? 'badge-gold' :
                          c.type === 'makeup' ? 'badge-purple' : 'badge-blue'
                        }`} style={c.type === 'makeup' ? { background: '#f5f3ff', color: '#7c3aed' } : {}}>
                          {c.type === 'holiday' ? 'HOLIDAY' : 
                           c.type === 'emergency' ? 'EMERGENCY' : 
                           c.type === 'rescheduled' ? 'RESCHEDULED' :
                           c.type === 'makeup' ? 'MAKE-UP CLASS' : 'ANNOUNCEMENT'}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500, fontSize: '13px' }}>
                          {typeof c.reason === 'object'
                            ? <>
                                <span>{c.reason.en}</span>
                                {c.reason.zh && <span style={{ display: 'block', fontSize: '12px', color: '#888', marginTop: '2px' }}>{c.reason.zh}</span>}
                              </>
                            : c.reason /* legacy plain string */
                          }
                        </div>
                        {c.notified && <div style={{ fontSize: '10px', color: '#4caf86', marginTop: '2px' }}>🔔 {t('closures.notified', 'Notified')}</div>}
                      </td>
                      <td>
                        {c.scope === 'all' ? (
                          <span className="badge" style={{ background: '#f0f0f0', color: '#555' }}>{t('closures.allBatchesBadge', 'All Batches')}</span>
                        ) : (
                          <span className="badge badge-blue" title={c.affectedBatchIds?.map(b => tField(b.name)).join(', ')}>
                            {c.affectedBatchIds?.length} {t('closures.batchesBadge', 'Batches')}
                          </span>
                        )}
                      </td>
                      <td>
                        <button 
                          className="btn btn-outline" 
                          style={{ padding: '4px 8px', fontSize: '12px', borderColor: '#ff4d4f', color: '#ff4d4f' }}
                          onClick={() => handleDelete(c._id)}
                        >
                          {t('closures.revoke', 'Revoke')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
      </div>
    </Layout>
  );
}
